import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";

// Layouts
import AccessCardsContent from "@Layouts/components/AccessCardsContent/index.jsx";
import AccessDialog from "@/Layouts/components/Dialogs/AccessDialog";
import AccessLists from "@/Layouts/components/AccessLists";
import DoubleCheckingButton from "@/Layouts/components/Buttons/DoubleCheckingButton";
import FranchiseLogo from "@/Layouts/components/FranchiseLogo";

// MUI
import { Divider, Grid, Skeleton, Stack, Typography } from "@mui/material";

// Icons
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

const FranchiseLogoContent = (props) => {
	const { logo, loading = false, isSelected = false } = props;

	if (!logo) {
		return null;
	}

	return (
		<Stack>
			{loading ? (
				<Skeleton width={50} height={34} />
			) : (
				<Typography className={"font-dealers-brands-logos gray content-center h-[40px]"}>
					{logo.type === "image" ? <FranchiseLogo logo={logo.icon} isSelected={isSelected} /> : <>{logo.icon}</>}
				</Typography>
			)}
		</Stack>
	);
};
FranchiseLogoContent.propTypes = {
	logo: PropTypes.shape({
		icon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
		type: PropTypes.string,
	}),
	loading: PropTypes.bool,
	isSelected: PropTypes.bool,
};

export default function UserAccessView(props) {
	const {
		index = 0,
		title = "",
		logo = null,
		loading = false,
		divider = false,
		isEditable = false,
		addAmount = 1,
		adding = null,
		deleting = null,
		data = {
			actions: [],
			groups: [],
		},
		locationId = "",
		onAddActions = () => {},
		onDeleteActions = () => {},
		onAddGroups = () => {},
		onDeleteGroups = () => {},
		onDeleteLocation = () => {},
	} = props;

	const [deleteAction, setDeleteAction] = useState(null);
	const [deleteGroup, setDeleteGroup] = useState(null);

	const addedActions = useRef([]);
	const addedGroups = useRef([]);

	const [openGroups, setOpenGroups] = useState(false);
	const [openActions, setOpenActions] = useState(false);

	const handleCloseActions = () => {
		setOpenActions(false);
		addedActions.current = [];
	};

	const handleCloseGroups = () => {
		setOpenGroups(false);
		addedGroups.current = [];
	};

	const handleAddActions = () => {
		onAddActions?.(addedActions.current, index);
		addedActions.current = [];
		setOpenActions(false);
	};

	const handleDeleteAction = (action) => {
		setDeleteAction(action);
		onDeleteActions?.([action], index);
	};

	const handleAddGroups = () => {
		onAddGroups?.(addedGroups.current, index);
		addedGroups.current = [];
		setOpenGroups(false);
	};

	const handleDeleteGroup = (group) => {
		setDeleteGroup(group);
		onDeleteGroups?.([group], index);
	};

	const handleDeleteLocation = () => {
		onDeleteLocation?.(index);
	};

	useEffect(() => {
		return () => {
			setOpenActions(false);
			setOpenGroups(false);
			addedActions.current = [];
			addedGroups.current = [];
		};
	}, [props]);

	return (
		<Grid item className="scrollable-content" id={index} xs={12} style={{ scrollMarginTop: "155px" }}>
			<AccessDialog title={"Actions"} open={openActions} onCloseAction={handleCloseActions} onAcceptAction={handleAddActions}>
				<AccessLists
					searchable
					accessType={"actions"}
					access={data.actions}
					locationId={locationId}
					titleField={"actionName"}
					descriptionField={"actionDescription"}
					onChangeSelection={(selection) => {
						addedActions.current = selection;
					}}
				/>
			</AccessDialog>

			<AccessDialog title={"Groups"} open={openGroups} onCloseAction={handleCloseGroups} onAcceptAction={handleAddGroups}>
				<AccessLists
					searchable
					accessType={"groups"}
					access={data.groups}
					locationId={locationId}
					titleField={"groupName"}
					descriptionField={"groupDescription"}
					onChangeSelection={(selection) => {
						addedGroups.current = selection;
					}}
				/>
			</AccessDialog>

			<Grid container alignItems={"center"}>
				<Grid item>
					<Stack direction={"row"} alignItems="center" alignContent={"center"} spacing={1}>
						<FranchiseLogoContent logo={logo} loading={loading} isSelected={false} />
						<Stack>
							{loading ? (
								<Skeleton width={260} height={34} />
							) : (
								<Typography className="regular-montserrat-label fs-24">{title}</Typography>
							)}
						</Stack>
					</Stack>
				</Grid>

				<Grid item ml={1}>
					{loading ? (
						<Skeleton variant="circular" width={35} height={35} sx={{ marginLeft: "10px" }} />
					) : (
						<DoubleCheckingButton
							title={"Remove Location"}
							endIcon={<DeleteOutlineOutlinedIcon sx={{ marginLeft: "-4px" }} />}
							mainButtonClasses={"btn-simple-remove btn-pack-remove"}
							disabled={!isEditable}
							mainSx={{ marginLeft: "10px" }}
							confirmationSx={{ right: 0, left: "10px", width: "150px !important" }}
							onDeleteAction={handleDeleteLocation}
						/>
					)}
				</Grid>
			</Grid>

			<Grid container mt={2}>
				<AccessCardsContent
					index={props?.index}
					isSingleLevel={true}
					locationId={props.locationId}
					data={data.groups}
					cardTitle={"groupName"}
					cardContent={"groupDescription"}
					cardClasses={"action-card-small-padding"}
					cardProperties={{
						subheader: true,
					}}
					title={"Groups"}
					buttonTitle={"Add New Group"}
					addAmount={addAmount}
					loading={loading}
					isEditable={isEditable}
					isContentIncluded={true}
					deleteDefinition={{
						identifier: "_id",
						action: deleteGroup,
					}}
					isAdding={adding?.groups}
					isDeleting={deleting?.group}
					onOpenAction={() => setOpenGroups(true)}
					onDeleteAction={handleDeleteGroup}
				/>

				<AccessCardsContent
					index={props?.index}
					locationId={props.locationId}
					data={data.actions}
					rootIdentifier={"category"}
					cardTitle={"actionName"}
					cardContent={"actionDescription"}
					title={"Actions"}
					titleColor={"text-gray-text"}
					cardProperties={{
						subheader: true,
					}}
					buttonTitle={"Add New Action"}
					containerProps={{
						mt: 4,
					}}
					addAmount={props.addAmount}
					loading={props.loading}
					isEditable={props.isEditable}
					deleteDefinition={{
						identifier: "actionKey",
						action: deleteAction,
					}}
					isAdding={adding?.actions}
					isDeleting={deleting?.action}
					isContentIncluded={true}
					onOpenAction={() => setOpenActions(true)}
					onDeleteAction={handleDeleteAction}
				/>
			</Grid>

			{divider && (
				<Grid container justifyContent={"center"}>
					<Grid item xs={7}>
						<Divider className="!border-disabled" sx={{ margin: "50px 0px 40px" }} />
					</Grid>
				</Grid>
			)}
		</Grid>
	);
}

UserAccessView.propTypes = {
	index: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
	title: PropTypes.string,
	logo: PropTypes.object,
	loading: PropTypes.bool,
	divider: PropTypes.bool,
	isEditable: PropTypes.bool,
	addAmount: PropTypes.number,
	adding: PropTypes.object,
	deleting: PropTypes.object,
	data: PropTypes.object,
	locationId: PropTypes.string,
	onAddActions: PropTypes.func,
	onDeleteActions: PropTypes.func,
	onAddGroups: PropTypes.func,
	onDeleteGroups: PropTypes.func,
	onDeleteLocation: PropTypes.func,
};
